import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { EventVenueMap } from 'src/components/Events/VenueMap/EventVenueMap';
import { CompListingsPreview } from 'src/components/Listings/CompListingsPreview';
import { CompListingsPreviewV2 } from 'src/components/Listings/CompListingsPreview/CompListingsPreviewV2';
import { Content, FormatContent } from 'src/contexts/ContentContext';
import { Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import {
  BulkListingPriceCalculationResult,
  Event,
  Feature,
  Listing,
  ListingDetailsAutoPricingSectionUpdatesV2,
} from 'src/WebApiController';

import * as styles from './BulkEditAutoPricingSettingsSidePanel.css';
import {
  useBulkEditAutoPricingSettingsSidePanelEventMapProps,
  useBulkGetListingPriceCalculationsInEvent,
} from './BulkEditAutoPricingSettingsSidePanel.hooks';

export const BulkEditAutoPricingSettingsSidePanel = ({
  event,
  selectedListings,
  show,
  onPreviewCalculated,
}: {
  event: Event;
  selectedListings: Listing[];
  show?: boolean;
  onPreviewCalculated?: (
    result: BulkListingPriceCalculationResult | null
  ) => void;
}) => {
  const hasYourListingsInCompsFeature = useUserHasFeature(
    Feature.YourListingsInComps
  );
  const { bulkListingPriceCalculationResult, isLoading } =
    useBulkGetListingPriceCalculationsInEvent(event, selectedListings);

  useEffect(() => {
    if (onPreviewCalculated) {
      onPreviewCalculated(bulkListingPriceCalculationResult ?? null);
    }
  }, [bulkListingPriceCalculationResult, onPreviewCalculated]);

  const eventVenueMapProps =
    useBulkEditAutoPricingSettingsSidePanelEventMapProps(
      event.viagId!,
      selectedListings,
      bulkListingPriceCalculationResult?.compListings
    );

  const { watch } = useFormContext<ListingDetailsAutoPricingSectionUpdatesV2>();
  const { compListingExcludeFanInventory } = watch();

  const sanitizedSelectedListings = useMemo(
    () => selectedListings.filter((l) => l.availQty > 0),
    [selectedListings]
  );

  if (!show) {
    return null;
  }

  return (
    <Stack direction="column" className={styles.mainContainerFull}>
      <Stack gap={'xl'} className={styles.sectionContainer}>
        <div className={styles.mapContainerFull}>
          <EventVenueMap
            {...eventVenueMapProps}
            infoIconContent={<Content id={ContentId.AutoPricingDisclaimer} />}
          />
        </div>
        <Stack direction="column" className={styles.compsContainerFull}>
          <Stack className={styles.compsContainerHeader}>
            <FormatContent
              id={FormatContentId.MarketplaceListings}
              params={[sanitizedSelectedListings.length.toString()]}
            />
          </Stack>

          {hasYourListingsInCompsFeature ? (
            <CompListingsPreviewV2
              isLoading={isLoading}
              showListingsCarousel
              listings={sanitizedSelectedListings}
              listingPriceCalculations={
                bulkListingPriceCalculationResult?.listingPriceCalculations ??
                []
              }
              compListings={
                bulkListingPriceCalculationResult?.compListings ?? []
              }
              isExcludingFanInventory={Boolean(
                compListingExcludeFanInventory?.value
              )}
              selectedListingIds={
                sanitizedSelectedListings?.map((l) => l.id) ?? []
              }
            />
          ) : (
            <CompListingsPreview
              isLoading={isLoading}
              isDetailPanel={false}
              listings={sanitizedSelectedListings}
              listingPriceCalculations={
                bulkListingPriceCalculationResult?.listingPriceCalculations ??
                []
              }
              compListings={
                bulkListingPriceCalculationResult?.compListings ?? []
              }
              sellerCompListings={
                bulkListingPriceCalculationResult?.sellerCompListings ?? []
              }
              isExcludingFanInventory={Boolean(
                compListingExcludeFanInventory?.value
              )}
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
